'use client'
import type { FC } from 'react'
import { useState } from 'react'
import type { IGrandSection } from 'ui'
import { Carousel, GrandSection } from 'ui'
import type { BoxProps } from '@chakra-ui/react'
import { Skeleton } from '@chakra-ui/react'
import type { DeliveryDTO, ProductLightDTO } from 'ecosystem'
import { type GrandSectionHeadingLink } from 'ui/storefront/layout/GrandSection'
import type { CarouselItemSize } from 'ui/common/Carousel/constants'
import ProductListItemComponent, { type ProductListItemProps } from './ProductListItem'

export interface IFeaturedProducts extends IGrandSection {
  items: ProductLightDTO[]
  onBuy: (item: ProductLightDTO) => void
  onProductRequest: (item: ProductLightDTO) => void
  productsUrlPath: string
  deliveryOptions: DeliveryDTO[] | null
  title?: string
  headingTag?: any
  itemContainerProps?: Omit<BoxProps, 'gap' | 'children'>
  headingLink?: GrandSectionHeadingLink
  carouselItemsByBreakpoint?: CarouselItemSize
  customComponents?: {
    ProductListItem?: FC<ProductListItemProps>
  }
  isLoading?: boolean
}

export const FeaturedProducts: FC<IFeaturedProducts> = ({
  deliveryOptions,
  title,
  items,
  headingTag,
  onBuy,
  onProductRequest,
  productsUrlPath,
  itemContainerProps,
  headingLink,
  carouselItemsByBreakpoint,
  customComponents,
  isLoading,
  ...props
}) => {
  const [activeItem, setActiveItem] = useState(0)
  const { ProductListItem = ProductListItemComponent } = customComponents || {}

  return (
    <GrandSection {...{ title, headingTag, headingLink }} {...props}>
      <Carousel
        gap={20}
        {...{ activeItem, setActiveItem, itemContainerProps, carouselItemsByBreakpoint }}
      >
        {isLoading || !items?.length
          ? Array.from({
              length: carouselItemsByBreakpoint ? carouselItemsByBreakpoint.lg : 6
            }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key -- need index
              <Skeleton height="380px" key={index} w="100%" />
            ))
          : items.map((item) => (
              <ProductListItem
                showsPrice
                key={item.id}
                w="full"
                imageProps={{
                  sizes: '(max-width: 52em) 100vw, (max-width: 80em) 30vw, 180px'
                }}
                {...{ item, deliveryOptions, onBuy, onProductRequest, productsUrlPath }}
              />
            ))}
      </Carousel>
    </GrandSection>
  )
}
